// 「全球投资」板块的十大投资工具,框架取自安德鲁·克雷格《如何配置全球资产》
// (How to Own the World):每个工具先讲底层逻辑,后续子项逐步扩展。
// 文字为自撰概括,非书中原文。

export interface InvestTool {
  /// 路由/注册表 id 前缀,如 "cash" → /invest/cash-logic
  id: string;
  /// 侧栏分组标题,如「1. 现金」
  title: string;
  /// 底层逻辑:是什么 / 收益从哪来 / 主要风险 / 在组合中的角色
  logic: { heading: string; body: string }[];
}

export const INVEST_TOOLS: InvestTool[] = [
  {
    id: 'cash',
    title: '1. 现金',
    logic: [
      {
        heading: '是什么',
        body: '活期与短期存款、货币基金等最高流动性的资产,是所有其他投资的"弹药库"。',
      },
      {
        heading: '收益从哪来',
        body: '利息。名义上几乎无波动,收益率跟随央行政策利率。',
      },
      {
        heading: '主要风险',
        body: '通胀是现金最大的敌人:名义安全、实际购买力持续被侵蚀。长期持有大量现金,几乎必然跑输通胀。',
      },
      {
        heading: '组合角色',
        body: '应急储备(3-6 个月开支)+ 等待机会的干火药;不是长期增值工具。',
      },
    ],
  },
  {
    id: 'property',
    title: '2. 房产',
    logic: [
      {
        heading: '是什么',
        body: '自住房与投资性物业(住宅、商铺、REITs)。多数家庭最大的单一资产。',
      },
      {
        heading: '收益从哪来',
        body: '租金现金流 + 资本增值;银行按揭带来的杠杆会同时放大收益与亏损。',
      },
      {
        heading: '主要风险',
        body: '流动性差、交易成本高、区域集中度高;利率上行直接抬高持有成本、压低估值。',
      },
      {
        heading: '组合角色',
        body: '抗通胀的实物资产;但要警惕"身家全押在一套房"的隐性集中风险,REITs 是分散化的替代入口。',
      },
    ],
  },
  {
    id: 'bonds',
    title: '3. 债券',
    logic: [
      {
        heading: '是什么',
        body: '把钱借给政府或企业的债权凭证,约定票息与到期还本。',
      },
      {
        heading: '收益从哪来',
        body: '票息 + 价格变动。利率下行时债价上涨,反之下跌;久期越长对利率越敏感。',
      },
      {
        heading: '主要风险',
        body: '利率风险、信用违约风险,以及"名义安全、实际缩水"的通胀风险——高通胀期国债也可能大幅跑输。',
      },
      {
        heading: '组合角色',
        body: '传统上是股票的压舱石(股债负相关时),提供稳定现金流;高通胀环境下对冲效果会失灵。',
      },
    ],
  },
  {
    id: 'equities',
    title: '4. 股票',
    logic: [
      {
        heading: '是什么',
        body: '企业所有权的份额,分享企业利润与成长。',
      },
      {
        heading: '收益从哪来',
        body: '盈利增长 + 股息 + 估值变化。长周期看是回报最高的主流资产类别。',
      },
      {
        heading: '主要风险',
        body: '波动大、回撤深(-50% 并不罕见);个股还有归零风险。短钱入市是大忌。',
      },
      {
        heading: '组合角色',
        body: '长期增值的发动机。对多数人而言,买"全世界"的低成本指数比选个股胜率高得多。',
      },
    ],
  },
  {
    id: 'commodities',
    title: '5. 大宗商品',
    logic: [
      {
        heading: '是什么',
        body: '能源、农产品、工业金属与贵金属等实物资产,黄金白银是其中特殊的货币类商品。',
      },
      {
        heading: '收益从哪来',
        body: '价格周期。商品本身不产生现金流,涨跌由供需与货币购买力驱动。',
      },
      {
        heading: '主要风险',
        body: '波动剧烈、周期长;通过期货持有还有展期成本。',
      },
      {
        heading: '组合角色',
        body: '通胀与货币贬值的对冲。克雷格极简方案里专门配置贵金属,正是防备法币购买力下滑。',
      },
    ],
  },
  {
    id: 'funds',
    title: '6. 基金',
    logic: [
      {
        heading: '是什么',
        body: '集合投资工具:主动管理基金、指数基金、ETF、投资信托等,一笔钱买到一篮子资产。',
      },
      {
        heading: '收益从哪来',
        body: '底层资产的回报,减去费用。费用是少数确定的变量——长期复利下 1% 的费差影响巨大。',
      },
      {
        heading: '主要风险',
        body: '主动基金多数长期跑不赢指数;高费率、风格漂移、规模膨胀都会侵蚀回报。',
      },
      {
        heading: '组合角色',
        body: '普通人配置全球资产的主通道。低成本全球指数 ETF 是"拥有全世界"最简单的实现方式。',
      },
    ],
  },
  {
    id: 'insurance',
    title: '7. 保险',
    logic: [
      {
        heading: '是什么',
        body: '带投资属性的保险类产品(储蓄险、年金、投连险),以及作为财务地基的保障型保险。',
      },
      {
        heading: '收益从哪来',
        body: '保险公司代为投资的底层组合收益,扣除保障成本与各层费用。',
      },
      {
        heading: '主要风险',
        body: '费用结构复杂且不透明、流动性差、提前退保损失大;"保障"与"投资"混在一起往往两头都不划算。',
      },
      {
        heading: '组合角色',
        body: '先配足纯保障(定期寿险、医疗、重疾),投资归投资;把投资型保单当理财主力通常不是好主意。',
      },
    ],
  },
  {
    id: 'forex',
    title: '8. 外汇',
    logic: [
      {
        heading: '是什么',
        body: '不同法币之间的兑换与持仓,全球交易量最大的市场。',
      },
      {
        heading: '收益从哪来',
        body: '汇率变动与利差(carry)。零和属性强,长期期望收益接近零减成本。',
      },
      {
        heading: '主要风险',
        body: '高杠杆下波动被急剧放大,散户短线交易胜率极低。',
      },
      {
        heading: '组合角色',
        body: '对普通投资者,外汇的意义主要是"币种分散"——持有以不同货币计价的资产,而非投机汇率本身。',
      },
    ],
  },
  {
    id: 'derivatives',
    title: '9. 衍生品',
    logic: [
      {
        heading: '是什么',
        body: '价值派生于底层资产的合约:期货、期权、差价合约(CFD)、互换等。',
      },
      {
        heading: '收益从哪来',
        body: '对底层价格方向或波动率的判断;自带杠杆,盈亏都被放大。',
      },
      {
        heading: '主要风险',
        body: '杠杆爆仓、时间价值衰减、对手方风险;是绝大多数散户亏损最快的场所。',
      },
      {
        heading: '组合角色',
        body: '专业工具:用于对冲(如保护性认沽)或增强收益,不适合作为普通人的主要持仓。',
      },
    ],
  },
  {
    id: 'crypto',
    title: '10. 加密资产',
    logic: [
      {
        heading: '是什么',
        body: '基于区块链的数字资产:比特币(稀缺性叙事的"数字黄金")、以太坊等公链资产及其生态代币。',
      },
      {
        heading: '收益从哪来',
        body: '采用率提升与货币贬值叙事驱动的价格重估;绝大多数币种没有现金流支撑。',
      },
      {
        heading: '主要风险',
        body: '极端波动(-80% 级别回撤)、监管与托管风险、大量项目归零。',
      },
      {
        heading: '组合角色',
        body: '高风险卫星仓位:小比例(能承受全亏的仓位)参与非对称上行,别让它决定你的财务命运。',
      },
    ],
  },
];
